import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonPicker } from './PersonPicker';
import { apiFetch } from '../lib/api';
import type { Person, Marriage, MarriageStatus } from '@nasabtree/shared-types';

interface AddMarriageDialogProps {
  treeId: string;
  person: Person;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
  onPersonsChanged: () => void;
  defaultFamilyName?: string;
}

const inputCls =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors';

export function AddMarriageDialog({
  treeId,
  person,
  open,
  onOpenChange,
  onCreated,
  onPersonsChanged,
  defaultFamilyName,
}: AddMarriageDialogProps) {
  const { t } = useTranslation();
  const [spouseId, setSpouseId] = useState<string | null>(null);
  const [wifeOrder, setWifeOrder] = useState(1);
  const [status, setStatus] = useState<MarriageStatus>('married');
  const [marriedDateHijri, setMarriedDateHijri] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const backdropRef = useRef<HTMLDivElement>(null);

  const isMale = person.gender === 'male';

  function resetForm() {
    setSpouseId(null);
    setWifeOrder(1);
    setStatus('married');
    setMarriedDateHijri('');
    setNotes('');
    setError('');
    setSaving(false);
  }

  function close() {
    resetForm();
    onOpenChange(false);
  }

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  async function handleSave() {
    if (!spouseId) return;
    setSaving(true);
    setError('');

    const body = {
      husband_id: isMale ? person.id : spouseId,
      wife_id: isMale ? spouseId : person.id,
      wife_order: wifeOrder,
      status,
      married_date_hijri: marriedDateHijri || null,
      notes: notes || null,
    };

    const res = await apiFetch<Marriage>(`/api/trees/${treeId}/marriages`, {
      method: 'POST',
      body: JSON.stringify(body),
    });

    if (res.ok) {
      onCreated();
      onPersonsChanged();
      close();
    } else {
      setError(res.error);
    }
    setSaving(false);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div
        ref={backdropRef}
        className="absolute inset-0 bg-black/40"
        onClick={(e) => { if (e.target === backdropRef.current) close(); }}
      />
      <div className="relative bg-white rounded-2xl shadow-xl p-6 w-full max-w-md z-50 mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-gray-900 mb-4">
          {t('relationships.addSpouse')}
        </h2>

        <div className="space-y-4">
          {error && (
            <div className="text-red-600 text-sm rounded-lg bg-red-50 border border-red-200 px-3 py-2">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {isMale ? t('persons.mother') : t('persons.father')}
              {' '}
              <span className="text-red-400">*</span>
            </label>
            <PersonPicker
              treeId={treeId}
              value={spouseId}
              onChange={(id) => { setSpouseId(id); }}
              excludeIds={[person.id]}
              genderFilter={isMale ? 'female' : 'male'}
              defaultFamilyName={defaultFamilyName}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('marriage.wifeOrder')}
            </label>
            <select
              value={wifeOrder}
              onChange={(e) => setWifeOrder(Number(e.target.value))}
              className={inputCls}
            >
              {[1, 2, 3, 4].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('marriage.status')}
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as MarriageStatus)}
              className={inputCls}
            >
              <option value="married">{t('marriage.married')}</option>
              <option value="divorced">{t('marriage.divorced')}</option>
              <option value="widowed">{t('marriage.widowed')}</option>
              <option value="annulled">{t('marriage.annulled')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('marriage.marriedDate')}
            </label>
            <input
              dir="ltr"
              placeholder="YYYY-MM-DD"
              value={marriedDateHijri}
              onChange={(e) => setMarriedDateHijri(e.target.value)}
              className={inputCls}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('marriage.notes')}
            </label>
            <textarea
              dir="auto"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={inputCls}
            />
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={close}
              className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              disabled={saving || !spouseId}
              onClick={() => void handleSave()}
              className="bg-brand-600 text-white rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors shadow-sm"
            >
              {saving ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={close}
          className="absolute top-4 end-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
